/**
 * Whether the BAM plan picker should ignore clicks while a payment is in flight.
 *
 * @param isPaymentSubmitting - Checkout payment request is in progress
 * @returns True when plan selection must be locked
 */
export const shouldLockPlanSelection = (
	isPaymentSubmitting: boolean,
): boolean => isPaymentSubmitting;

/**
 * Whether the membership modal may close.
 *
 * A backdrop, Escape, X, or Not Interested click during submit drops the
 * buyer out of checkout with no error and no charge.
 *
 * @param isPaymentSubmitting - Checkout payment request is in progress
 * @returns False while a payment is submitting
 */
export const shouldDismissMembershipModal = (
	isPaymentSubmitting: boolean,
): boolean => !isPaymentSubmitting;

/**
 * Whether PayPal can be offered for the selected membership plan.
 *
 * PayPal bills its own plan amount, so it is hidden unless that amount equals
 * the Stripe price shown on the card.
 *
 * @param options.paypalPlanId - PayPal plan mapped to the Stripe price
 * @param options.paypalPlanAmount - Regular amount the PayPal plan bills, in cents
 * @param options.priceAmount - Stripe price shown on the card, in cents
 * @param options.hasSale - A sale is running
 * @param options.hasPromoCode - A promo code is applied
 * @param options.hasTrial - The plan starts with a trial
 * @param options.isStripeActive - Buyer already has an active Stripe subscription
 * @param options.hasJoiningFee - A joining fee applies, which PayPal plans cannot charge
 * @returns True when the PayPal button should be shown
 */
export const canUsePayPalCheckout = (options: {
	paypalPlanId?: string;
	paypalPlanAmount?: number;
	priceAmount?: number | null;
	hasSale: boolean;
	hasPromoCode: boolean;
	hasTrial: boolean;
	isStripeActive: boolean;
	hasJoiningFee?: boolean;
}): boolean =>
	!options.hasJoiningFee &&
	!!options.paypalPlanId &&
	options.paypalPlanAmount != null &&
	options.paypalPlanAmount === options.priceAmount &&
	(options.hasSale || !options.hasPromoCode) &&
	!options.hasTrial &&
	!options.isStripeActive;

/**
 * Whether a recoverable decline should switch the UI to the new-card form.
 *
 * When the buyer is already on PaymentElement, forcing the switch remounts
 * Elements and wipes into "Loading card form…".
 *
 * @param options.alreadyAddingNewPaymentMethod - New-card form is already open
 * @returns True only when switching from a saved-card (or empty) view
 */
export const shouldOpenNewCardFormAfterDecline = (options: {
	alreadyAddingNewPaymentMethod: boolean;
}): boolean => !options.alreadyAddingNewPaymentMethod;
